// Spectator session: the passcode login returns an opaque bearer token that
// every public Convex function takes as `sessionToken`. The token lives in
// localStorage (wrapped — it may be unavailable) and in React state.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from 'convex/react';
import type { FunctionArgs, FunctionReference, FunctionReturnType } from 'convex/server';
import { api } from '../../convex/_generated/api';
import type { SpectatorRole } from '../../convex/shared/validators';
import { readLocal, STORAGE_KEYS, writeLocal } from './storage';

type SessionValue = {
  token: string | null;
  role: SpectatorRole | null;
  label: string | null;
  isAdmin: boolean;
  /** True while a stored token is being checked against the server. */
  loading: boolean;
  signIn: (token: string) => void;
  signOut: () => void;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => readLocal(STORAGE_KEYS.token));
  const me = useQuery(api.spectate.auth.me, token ? { sessionToken: token } : 'skip');
  const logout = useMutation(api.spectate.auth.logout);

  const signIn = useCallback((t: string) => {
    writeLocal(STORAGE_KEYS.token, t);
    setToken(t);
  }, []);

  const signOut = useCallback(() => {
    const current = token;
    writeLocal(STORAGE_KEYS.token, null);
    setToken(null);
    if (current) void logout({ sessionToken: current }).catch(() => undefined);
  }, [logout, token]);

  const value = useMemo<SessionValue>(
    () => ({
      // A token the server no longer knows (expired, logged out elsewhere) is treated as none.
      token: me === null ? null : token,
      role: me?.role ?? null,
      label: me?.label ?? null,
      isAdmin: me?.role === 'admin',
      loading: token !== null && me === undefined,
      signIn,
      signOut,
    }),
    [me, token, signIn, signOut],
  );

  // The server forgot this token (expired, purged, logged out elsewhere): drop it.
  useEffect(() => {
    if (token && me === null) {
      writeLocal(STORAGE_KEYS.token, null);
      setToken(null);
    }
  }, [me, token]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

type SessionQuery = FunctionReference<'query', 'public', { sessionToken: string }>;
type ArgsWithoutToken<Q extends SessionQuery> = Omit<FunctionArgs<Q>, 'sessionToken'>;

/**
 * `useQuery` for the spectator-gated public queries: injects `sessionToken`,
 * and skips while signed out. Pass `'skip'` to skip explicitly.
 */
export function useSimQuery<Q extends SessionQuery>(query: Q, args: ArgsWithoutToken<Q> | 'skip'): FunctionReturnType<Q> | undefined {
  const { token } = useSession();
  const full = args === 'skip' || !token ? 'skip' : ({ ...args, sessionToken: token } as FunctionArgs<Q>);
  // useQuery's rest-args typing cannot follow a generic Q; the shape is checked above.
  return useQuery(query as FunctionReference<'query'>, full as never) as FunctionReturnType<Q> | undefined;
}

/** A mutation that injects `sessionToken` (throws when signed out). */
export function useSimMutation<M extends FunctionReference<'mutation', 'public', { sessionToken: string }>>(mutation: M) {
  const { token } = useSession();
  const run = useMutation(mutation);
  return useCallback(
    (args: Omit<FunctionArgs<M>, 'sessionToken'>): Promise<FunctionReturnType<M>> => {
      if (!token) return Promise.reject(new Error('Not signed in'));
      // useMutation's rest-args typing cannot follow a generic M; the shape is fixed above.
      const call = run as unknown as (a: FunctionArgs<M>) => Promise<FunctionReturnType<M>>;
      return call({ ...args, sessionToken: token } as FunctionArgs<M>);
    },
    [run, token],
  );
}
